import React, { useState, useMemo } from 'react';
import {
  Box, Table, TableBody, TableCell, TableHead, TableRow,
  Paper, Chip, makeStyles,
} from '@material-ui/core';
import ProcedureNumberCell from './ProcedureNumberCell';
import { INPROCESS_DATA, mergeTabData, fmtDate } from './manufacturingTabData';
import { inProcessDoc, openDoc } from './mfgDocLinks';
import Pagination from '../../logisticsDashboard/components/Pagination';


const useStyles = makeStyles(() => ({
  tableContainer: {
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    margin: '8px 0',
    overflowX: 'hidden',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  scrollArea: {
    width: '100%',
    maxHeight: 440,
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  headerRow: {
    backgroundColor: '#F9F9FC',
    '& th': {
      backgroundColor: '#F9F9FC',
      fontWeight: 600,
      fontSize: '0.88rem',
      color: '#333',
      padding: '8px 10px',
      borderBottom: '1px solid #e0e0e0',
      whiteSpace: 'normal',
      wordBreak: 'normal',
      overflowWrap: 'normal',
      verticalAlign: 'bottom',
      position: 'sticky',
      top: 0,
      zIndex: 1,
    },
  },
  bodyRow: {
    '&:hover': { backgroundColor: '#f5f5f5' },
    '& td': {
      fontSize: '0.9rem',
      padding: '8px 10px',
      borderBottom: '1px solid #eeeeee',
      color: '#444',
      verticalAlign: 'top',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    },
  },
  chipApproved: {
    backgroundColor: '#4CAF50',
    color: '#fff',
    fontSize: '0.82rem',
    height: '22px',
  },
  chipRejected: {
    backgroundColor: '#d32f2f',
    color: '#fff',
    fontSize: '0.82rem',
    height: '22px',
  },
}));

const COLUMNS = [
  'Phase / Step\nNumber',
  'Operation\nDescription',
  'Method\nID',
  'Sample\nDescription',
  'QC Receipt\nDate',
  'Sample ID',
  'Specification\nLimit',
  'Result\nValue',
  'UOM',
  'Result',
  'Analytical\nReport',
];

// Each column takes the API value where present, otherwise falls back to the
// "In-Process Tests" sheet of the batch's Manufacturing Collaboration workbook
// (see mergeTabData). Procedure Number is "Unit Procedure.Operation.Phase".
const InProcessTestsTab = ({ data, loading, batchNumber, pageSize = 10 }) => {
  const classes = useStyles();
  const PAGE_SIZE = pageSize || 10;
  const rows = mergeTabData(data, INPROCESS_DATA, batchNumber);

  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [rows, safePage, PAGE_SIZE]
  );

  const renderResult = (result) => {
    if (result === 'Approved' || result === 'Pass') {
      return <Chip label={result} className={classes.chipApproved} size="small" />;
    }
    if (result === 'Rejected' || result === 'Fail') {
      return <Chip label={result} className={classes.chipRejected} size="small" />;
    }
    return result;
  };

  if (loading) return <Box style={{ textAlign: 'center', padding: 24 }}>Loading...</Box>;

  return (
    <Paper className={classes.tableContainer} elevation={0}>
      <div className={classes.scrollArea}>
        <Table stickyHeader size="small" style={{ width: '100%', tableLayout: 'auto' }}>
          <TableHead>
            <TableRow className={classes.headerRow}>
              {COLUMNS.map(col => (
                <TableCell key={col}>
                  {col.split('\n').map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < col.split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={COLUMNS.length} style={{ textAlign: 'center' }}>No data available</TableCell></TableRow>
            ) : pageRows.map((row, idx) => (
              <TableRow key={idx} className={classes.bodyRow}>
                <TableCell><ProcedureNumberCell value={row.procedureNumber} batchNumber={batchNumber} /></TableCell>
                <TableCell>{row.operationDescription}</TableCell>
                <TableCell>{row.methodId}</TableCell>
                <TableCell style={{ maxWidth: '140px' }}>{row.sampleDescription}</TableCell>
                <TableCell>{fmtDate(row.qcReceiptDate)}</TableCell>
                <TableCell>{row.sampleId}</TableCell>
                <TableCell>{row.specificationLimit}</TableCell>
                <TableCell>{row.resultValue}</TableCell>
                <TableCell>{row.uom}</TableCell>
                <TableCell>{renderResult(row.result)}</TableCell>
                <TableCell>
                  {inProcessDoc(batchNumber, row) ? (
                    <span
                      onClick={() => openDoc(inProcessDoc(batchNumber, row))}
                      style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}
                    >
                      Click To View
                    </span>
                  ) : (
                    <span style={{ color: '#9e9e9e' }}>—</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {rows.length > PAGE_SIZE && (
        <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
      )}
    </Paper>
  );
};

export default InProcessTestsTab;
